import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { LoadingSpinner } from '../common/LoadingSpinner';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { studentAPI } from '../../services/api';
import { isProfilePhotoDocumentType, effectiveFileRule } from '../../utils/documentType';
import { writeProfilePhoto } from '../../utils/profilePhotoUpload';


let DocumentPicker: any;
if (Platform.OS !== 'web') {
  DocumentPicker = require('react-native-document-picker').default;
}


interface NativePickedFile {
  uri: string;
  name: string;
  type: string;
  size?: number;
}

interface DocumentType {
  id: string;
  name: string;
  code: string;
  description?: string;
  allowedFileTypes: string;
  allowedFileSize: number;
}

interface UploadedDoc {
  id: string;
  name: string;
  raw: any;
}

interface Props {
  studentProfileId: string;
  /** The already-uploaded documents from the profile snapshot. */
  documents: any[];
  onPreview: (doc: any) => void;
  onDownload: (doc: any) => void;
  previewingId: string | null;
  downloadingId: string | null;
  onUploaded: () => void | Promise<void>;

  onProfilePhotoUpdated?: () => void;
}

const normalizeType = (d: any): DocumentType => ({
  id: d.Id || d.id,
  name: d.Name || d.name,
  code: d.Code || d.code,
  description: d.Description || d.description,
  allowedFileTypes: d.AllowedFileTypes || d.allowedFileTypes || '',
  allowedFileSize: d.AllowedFileSize || d.allowedFileSize || 0,
});


export function StudentDocumentTiles({
  studentProfileId,
  documents,
  onPreview,
  onDownload,
  previewingId,
  downloadingId,
  onUploaded,
  onProfilePhotoUpdated,
}: Props) {
  const [docTypes, setDocTypes] = useState<DocumentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [errorByType, setErrorByType] = useState<Record<string, string>>({});

  useEffect(() => {
    studentAPI.getDocumentTypes('ENROLLED')
      .then(({ data }) => setDocTypes((data || []).map(normalizeType)))
      .catch(() => setDocTypes([]))
      .finally(() => setLoading(false));
  }, []);

  const uploadedByName = new Map<string, UploadedDoc>(
    (documents || []).map((d: any) => {
      const name = (d.DocumentName || d.documentName || '').trim().toLowerCase();
      return [name, { id: d.Id || d.id || d.DocumentId || d.documentId, name, raw: d }];
    }),
  );

  const setError = (typeId: string, message: string | null) =>
    setErrorByType(prev => {
      const next = { ...prev };
      if (message) next[typeId] = message; else delete next[typeId];
      return next;
    });

  const uploadFile = useCallback(async (docType: DocumentType, file: File | NativePickedFile) => {
    setError(docType.id, null);
    setUploadingId(docType.id);
    try {
      const formData = new FormData();
      if (Platform.OS === 'web') {
        formData.append('File', file as File);
      } else {
        const native = file as NativePickedFile;
        formData.append('File', { uri: native.uri, name: native.name, type: native.type } as any);
      }
      await studentAPI.uploadStudentDocumentByProfile(docType.id, studentProfileId, formData);

      await onUploaded();


      if (isProfilePhotoDocumentType(docType)) {
        const photo = await writeProfilePhoto(studentProfileId, {
          name: (file as any).name,
          size: (file as any).size,
          value: Platform.OS === 'web'
            ? file
            : { uri: (file as NativePickedFile).uri, name: (file as NativePickedFile).name, type: (file as NativePickedFile).type },
        });
        if (photo.ok) {
          onProfilePhotoUpdated?.();
        } else {
          setError(docType.id, photo.message);
        }
      }
    } catch (err: any) {
      setError(docType.id,
        err?.response?.data?.Message || err?.response?.data?.message || 'Upload failed. Try again.');
    } finally {
      setUploadingId(null);
    }
  }, [studentProfileId, onUploaded, onProfilePhotoUpdated]);

  const pickAndUpload = useCallback((docType: DocumentType) => {
    // For the profile-photo type this narrows the school's rule to what the
    // photograph write also accepts, since the same bytes are stored as both
    // (see effectiveFileRule).
    const rule = effectiveFileRule(docType);
    const allowed = rule.extensions;
    const maxBytes = rule.maxMb * 1024 * 1024;

    if (Platform.OS === 'web') {
      const docObj = (globalThis as any).document;
      if (!docObj?.createElement) return;

      const input = docObj.createElement('input');
      input.type = 'file';
      if (allowed.length > 0) input.accept = allowed.map((t: string) => `.${t}`).join(',');
      input.onchange = (e: any) => {
        const file: File | undefined = e.target.files?.[0];
        if (!file) return;

        const ext = file.name.split('.').pop()?.toLowerCase() || '';
        if (allowed.length > 0 && !allowed.includes(ext)) {
          setError(docType.id, `Allowed: ${allowed.join(', ')}`);
          return;
        }
        if (maxBytes > 0 && file.size > maxBytes) {
          setError(docType.id, `Max size ${rule.maxMb} MB`);
          return;
        }
        void uploadFile(docType, file);
      };
      input.click();
      return;
    }

    // Native: open the OS document picker (Files / Google Drive / iCloud).
    (async () => {
      try {
        const result = await DocumentPicker.pickSingle({
          type: DocumentPicker.types.allFiles,
          copyTo: 'cachesDirectory',
        });

        const ext = (result.name || '').split('.').pop()?.toLowerCase() || '';
        if (allowed.length > 0 && !allowed.includes(ext)) {
          setError(docType.id, `Allowed: ${allowed.join(', ')}`);
          return;
        }
        if (maxBytes > 0 && (result.size || 0) > maxBytes) {
          setError(docType.id, `Max size ${rule.maxMb} MB`);
          return;
        }

        void uploadFile(docType, {
          uri: result.fileCopyUri || result.uri,
          name: result.name || `document.${ext}`,
          type: result.type || 'application/octet-stream',
          size: result.size ?? undefined,
        });
      } catch (err) {
        if (DocumentPicker.isCancel(err)) return;
        setError(docType.id, 'Failed to open the file picker. Please try again.');
      }
    })();
  }, [uploadFile]);

  if (loading) {
    return <LoadingSpinner size="small" message="Loading documents..." />;
  }

  if (docTypes.length === 0) {
    return (
      <Text style={styles.emptyText}>
        The school has not configured any documents to collect yet.
      </Text>
    );
  }

  return (
    <View style={styles.grid}>
      {docTypes.map(dt => {
        const uploaded = uploadedByName.get(dt.name.trim().toLowerCase());
        const isUploading = uploadingId === dt.id;
        const isPreviewing = uploaded && previewingId === uploaded.id;
        const isDownloading = uploaded && downloadingId === uploaded.id;
        const error = errorByType[dt.id];

        return (
          <View key={dt.id} style={[styles.tile, uploaded ? styles.tileDone : null]}>
            <View style={styles.tileHeader}>
              <View style={[styles.tileIcon, uploaded ? styles.tileIconDone : null]}>
                <FeatherIcon
                  name={uploaded ? 'check' : 'file-plus'}
                  size={18}
                  color={uploaded ? '#15803D' : '#4338CA'}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.tileName}>{dt.name}</Text>
                <Text style={styles.tileMeta}>
                  {effectiveFileRule(dt).extensions.join(', ').toUpperCase()} · Max {effectiveFileRule(dt).maxMb}MB
                </Text>
              </View>
            </View>

            {uploaded ? (
              <View style={styles.tileActions}>
                <TouchableOpacity style={styles.actionBtn}
                  disabled={!!isPreviewing}
                  onPress={() => onPreview(uploaded.raw)}>
                  {isPreviewing
                    ? <ActivityIndicator size="small" color="#4338CA" />
                    : (<><FeatherIcon name="eye" size={14} color="#4338CA" />
                      <Text style={styles.actionText}>Preview</Text></>)}
                </TouchableOpacity>
                <TouchableOpacity style={styles.actionBtn}
                  disabled={!!isDownloading}
                  onPress={() => onDownload(uploaded.raw)}>
                  {isDownloading
                    ? <ActivityIndicator size="small" color="#4338CA" />
                    : (<><FeatherIcon name="download" size={14} color="#4338CA" />
                      <Text style={styles.actionText}>Download</Text></>)}
                </TouchableOpacity>
                <TouchableOpacity style={styles.actionBtn}
                  disabled={isUploading}
                  onPress={() => pickAndUpload(dt)}>
                  {isUploading
                    ? <ActivityIndicator size="small" color="#B45309" />
                    : (<><FeatherIcon name="refresh-cw" size={14} color="#B45309" />
                      <Text style={[styles.actionText, { color: '#B45309' }]}>Replace</Text></>)}
                </TouchableOpacity>
              </View>
            ) : (
              <TouchableOpacity style={styles.uploadBtn}
                disabled={isUploading}
                onPress={() => pickAndUpload(dt)}>
                {isUploading
                  ? <ActivityIndicator size="small" color="#FFFFFF" />
                  : (<><FeatherIcon name="upload" size={14} color="#FFFFFF" />
                    <Text style={styles.uploadBtnText}>Upload</Text></>)}
              </TouchableOpacity>
            )}

            {error ? <Text style={styles.errorText}>{error}</Text> : null}
          </View>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  } as any,
  tile: {
    flexGrow: 1,
    flexBasis: 220,
    maxWidth: 340,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 14,
    backgroundColor: '#FFFFFF',
    padding: 14,
  },
  tileDone: {
    borderColor: '#BBF7D0',
    backgroundColor: '#F0FDF4',
  },
  tileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  } as any,
  tileIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: '#EEF2FF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileIconDone: {
    backgroundColor: '#DCFCE7',
  },
  tileName: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1E293B',
  },
  tileMeta: {
    fontSize: 11,
    color: '#64748B',
    marginTop: 2,
  },
  tileActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  } as any,
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
  } as any,
  actionText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#4338CA',
  },
  uploadBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 9,
    borderRadius: 9,
    backgroundColor: '#1E40AF',
  } as any,
  uploadBtnText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '700',
  },
  emptyText: {
    fontSize: 13,
    color: '#94A3B8',
    fontStyle: 'italic',
  },
  errorText: {
    marginTop: 8,
    fontSize: 12,
    color: '#EF4444',
  },
});
